import { Controller } from "@hotwired/stimulus"

/**
 * Auto Reload Controller
 * 
 * Automatically reloads Turbo frames based on URL changes and navigation events.
 * Used for keeping frame content synchronized with the current URL state.
 * 
 * Features:
 * - Reloads frames when URL parameters change
 * - Shows/hides loading indicators during frame loads
 * - Preserves search form state on search pages
 * - Handles Turbo navigation events
 * - Manages loading bar visibility and animations
 * 
 * Usage: Add data-controller="auto-reload" to Turbo frames that need automatic reloading
 */
export default class extends Controller {
  connect() {
    const currentPath = window.location.pathname
    const currentUrl = new URL(window.location.href)
    if (currentPath.includes('/search') && currentUrl.searchParams.has('q[term_cont]')) {
      return
    }

    this.reloadOnUrlChange()

    this.boundReloadOnUrlChange = this.reloadOnUrlChange.bind(this)
    window.addEventListener('popstate', this.boundReloadOnUrlChange)
    document.addEventListener('turbo:before-visit', () => this.boundReloadOnUrlChange())
    document.addEventListener('turbo:visit', () => this.boundReloadOnUrlChange())
    
    // Listen for turbo frame loading events
    this.element.addEventListener('turbo:before-frame-load', this.showLoading.bind(this))
    this.element.addEventListener('turbo:frame-load', this.hideLoading.bind(this))
    this.element.addEventListener('turbo:frame-load-error', this.hideLoading.bind(this))
    
    // Show loading immediately when controller connects
    this.showLoading()
  }

  disconnect() {
    // Clean up event listeners
    window.removeEventListener('popstate', this.boundReloadOnUrlChange)
    document.removeEventListener('turbo:before-visit', this.boundReloadOnUrlChange)
    document.removeEventListener('turbo:visit', this.boundReloadOnUrlChange)
    this.element.removeEventListener('turbo:before-frame-load', this.showLoading.bind(this))
    this.element.removeEventListener('turbo:frame-load', this.hideLoading.bind(this))
    this.element.removeEventListener('turbo:frame-load-error', this.hideLoading.bind(this))
  }

  showLoading() {
    const loadingBar = this.element.querySelector('.loading-bar')
    if (loadingBar) {
      loadingBar.style.display = 'flex'
      loadingBar.style.opacity = '1'
    } else {
      console.warn('[AutoReloadController] No loading bar found in element')
    }
  }

  hideLoading() {
    const loadingBar = this.element.querySelector('.loading-bar')
    if (loadingBar) {
      // Add a small delay to ensure the loading animation is visible
      setTimeout(() => {
        loadingBar.style.opacity = '0'
        setTimeout(() => {
          loadingBar.style.display = 'none'
        }, 200)
      }, 100)
    }
  }

  reloadOnUrlChange() {
    const currentUrl = new URL(window.location.href)
    const currentTab = currentUrl.searchParams.get('tab') || 'info'
    const frame = this.element
    const currentSrc = frame.src
    const currentPath = window.location.pathname
    const expectedTab = this.getExpectedTabFromPath(currentPath)

    if (currentPath.includes('/search') && currentUrl.searchParams.has('q[term_cont]')) {
      return
    }
    if (currentPath.includes('/search') && currentSrc && currentSrc.includes(`tab=${expectedTab}`)) {
      return
    }

    if (currentSrc && !currentSrc.includes(`tab=${expectedTab}`)) {
      const newSrc = currentSrc.replace(/tab=[^&]*/, `tab=${expectedTab}`)
      Turbo.visit(newSrc, { frame: frame.id })
      setTimeout(() => this.triggerBatchActionsUpdate(), 100)
    }
  }

  getExpectedTabFromPath(path) {
    // Map URL paths to expected tabs for projects index
    if (path.includes('/delete_projects')) {
      return 'delete_projects'
    } else if (path.includes('/new')) {
      return 'new'
    } else if (path.includes('/search')) {
      return 'search'
    } else if (path.match(/\/projects\/[^\/]+$/)) {
      // Project show page - extract tab from URL or default to 'info'
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('tab') || 'info'
    } else if (path.match(/\/variants\/[^\/]+$/)) {
      // Variant show page - extract tab from URL or default to 'info'
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('tab') || 'info'
    } else {
      return 'info' // default
    }
  }

  triggerBatchActionsUpdate() {
    const batchActionsControllers = document.querySelectorAll('[data-controller*="batch-actions"]')
    batchActionsControllers.forEach(element => {
      const controller = this.application.getControllerForElementAndIdentifier(element, 'batch-actions')
      if (controller && controller.updateActionCount) {
        controller.updateActionCount()
      }
    })
  }
} 